import * as React from 'react';
import { CSSProperties } from 'react';

// Styles
const styles: Record<string, CSSProperties> = {
  section: {
    padding: '70px 0',
    backgroundColor: '#fffaf0',
    position: 'relative',
    overflow: 'hidden'
  },
  patternBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.04,
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='52' height='26' viewBox='0 0 52 26' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ff5722' fill-opacity='0.1'%3E%3Cpath d='M10 10c0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6h-2c0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6 0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6 0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6zm25.464-1.95l8.486 8.486-1.414 1.414-8.486-8.486 1.414-1.414z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px',
    position: 'relative',
    zIndex: 1
  },
  heading: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    textAlign: 'center' as const,
    marginBottom: '48px',
    color: '#1f2937',
    background: 'linear-gradient(to right, #4f46e5, #7c3aed)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    display: 'inline-block'
  },
  subheading: {
    fontSize: '1.25rem',
    color: '#4b5563',
    textAlign: 'center' as const,
    maxWidth: '800px',
    margin: '0 auto 48px',
    fontWeight: 500
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '32px',
  },
  testimonial: {
    backgroundColor: 'white',
    padding: '32px',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(79, 70, 229, 0.1)',
    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out'
  },
  testimonialHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
  },
  avatar: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    marginRight: '16px',
    objectFit: 'cover' as const,
    border: '3px solid #4f46e5',
  },
  authorInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  authorName: {
    fontWeight: 'bold',
    color: '#1f2937',
    fontSize: '1.125rem',
    marginBottom: '4px'
  },
  authorRole: {
    fontSize: '0.875rem',
    color: '#6b7280',
  },
  quote: {
    fontStyle: 'italic',
    color: '#4b5563',
    lineHeight: '1.7',
    fontSize: '1.125rem',
    position: 'relative',
    paddingLeft: '24px',
  },
  quoteMark: {
    position: 'absolute',
    left: 0,
    top: 0,
    fontSize: '2rem',
    color: '#4f46e5',
    opacity: 0.3,
    fontFamily: 'Georgia, serif',
    lineHeight: '1'
  }
};

const TestimonialSection: React.FC = () => {
  const testimonials = [
    {
      id: 1,
      quote: "The roast was brutal but hilarious! I implemented the suggestions and saw a 32% increase in conversion rate within two weeks. Who knew my listing was so bad?",
      author: "Sarah K.",
      role: "Amazon Seller, Home & Kitchen",
      avatar: "https://randomuser.me/api/portraits/women/44.jpg"
    },
    {
      id: 2,
      quote: "I was crying from laughter and then crying from the truth bombs. Fixed my bullet points as suggested and my sales went up almost immediately. The AI was savage but spot-on!",
      author: "Mike T.",
      role: "FBA Seller, Sports & Outdoors",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg"
    },
    {
      id: 3,
      quote: "Worth every second! The roast pointed out issues I never would have noticed. My product went from page 3 to page 1 after implementing the changes. My ego is bruised but my wallet is happy.",
      author: "Jessica M.",
      role: "Private Label Seller, Beauty",
      avatar: "https://randomuser.me/api/portraits/women/68.jpg"
    }
  ];

  return (
    <section style={styles.section}>
      {/* Background Pattern */}
      <div style={styles.patternBackground} />
      
      <div style={styles.container}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={styles.heading}>What Our Users Say</h2>
          <p style={styles.subheading}>
            These fictional testimonials represent the real results sellers can achieve with our AI-powered listing analysis. 
            While the people are made up, the insights are 100% real.
          </p>
        </div>
        
        <div style={styles.grid}>
          {testimonials.map((testimonial) => (
            <div 
              key={testimonial.id}
              style={styles.testimonial}
            >
              <div style={styles.testimonialHeader}>
                <img 
                  src={testimonial.avatar} 
                  alt={testimonial.author}
                  style={styles.avatar}
                />
                <div style={styles.authorInfo}>
                  <h3 style={styles.authorName}>{testimonial.author} <span style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 'normal' }}>(Parody)</span></h3>
                  <p style={styles.authorRole}>{testimonial.role}</p>
                </div>
              </div>
              <div style={styles.quote}>
                <span style={styles.quoteMark}>"</span>
                {testimonial.quote}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialSection;
